import { Property } from 'csstype'

import { Button, SafeHtml } from '@thg-commerce/gravity-elements'
import {
  css,
  mq,
  Padding,
  padding,
  spacing,
  styled,
  Text,
  zIndex,
  ZIndexLevel,
} from '@thg-commerce/gravity-theme'
import { TextStyling } from '@thg-commerce/gravity-theme/typography/typography'

import { BarIndicatorPosition, CircularIndicatorType } from './theme'

export const StyledHiddenCircularProgress = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  transform: rotate(45deg);
  top: 150px;
  background-color: ${(props) => props.theme.colors.palette.greys.white};
`

export const StyledTotalSummary = styled(SafeHtml)`
  ${Text('bodyText', 'alternate')};
  text-align: center;
`

export const Bar = styled.div<{ completedRatio: number }>`
  height: 100%;
  width: ${(props) => props.completedRatio}%;
  background-color: ${(props) => props.theme.colors.palette.brand.base};
  transition: 0.3s ease;
  border-radius: 8px;
`

export const StyledProgressBar = styled.div<{
  height?: number
  backgroundColor?: Property.Color
}>`
  width: 100%;
  height: ${(props) => props.height || 14}px;
  margin: auto 0;
  border-radius: 8px;
  background-color: ${(props) =>
    props.backgroundColor || props.theme.colors.palette.greys.grey};
`

export const ProgressBarContainer = styled.div<{
  position: BarIndicatorPosition
}>`
  width: 100%;
  display: flex;
  gap: ${spacing(1)};
  position: relative;
  flex-direction: column;
  ${(props) =>
    props.position === BarIndicatorPosition.FIXED &&
    `margin: 0 ${spacing(2)} ${spacing(1)} 0;`}
`

export const BarWrapper = styled.div<{
  position: BarIndicatorPosition
  padding: Padding
  height?: string | number
}>`
  ${(props) =>
    props.position === BarIndicatorPosition.FIXED &&
    css`
      position: fixed;
      bottom: 0;
      left: 0;
      border-top: 1px solid ${props.theme.colors.palette.greys.light};
      background-color: ${(props) => props.theme.colors.palette.greys.white};
      ${zIndex(ZIndexLevel.Base)}
      ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
        display: none;
      }
    `}

  width: 100%;
  height: ${(props) => props.height || '82px'};
  box-sizing: border-box;
  ${(props) => padding(props.padding)};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

export const CircularTextWrapper = styled.div<{
  shape: CircularIndicatorType
}>`
  position: absolute;
  text-align: center;
  ${(props) =>
    props.shape === CircularIndicatorType.FULL
      ? `
          transform: translateY(-50%);
          top: 50%;
        `
      : 'top: 70px;'};
`

export const CircularCurrentIndicator = styled.span<{
  textStyle: TextStyling
}>`
  ${(props) =>
    Text(
      props.textStyle?.entry || 'xlarge2',
      props.textStyle?.style || 'alternate',
    )};
  color: ${(props) => props.textStyle?.textColor || 'red'};
  top: 70px;
`

export const CircularItemsText = styled.p<{
  textStyle: TextStyling
  shape: CircularIndicatorType
}>`
  ${(props) => Text(props.textStyle?.entry, props.textStyle?.style)};
  color: ${(props) => props.textStyle?.textColor};
  margin-top: ${(props) =>
    props.shape === CircularIndicatorType.SEMI ? '12px' : 0};
`

export const StyledCircularProgress = styled.div<{
  progress: number
  shape: CircularIndicatorType
}>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: conic-gradient(
    ${(props) => props.theme.colors.palette.brand.base},
    ${(props) => props.progress}deg,
    #ededed 0deg
  );
  ${(props) =>
    props.shape === CircularIndicatorType.SEMI && 'transform: rotate(228deg);'}

  &::before {
    content: '';
    position: absolute;
    border-radius: 50%;
    background-color: ${(props) => props.theme.colors.palette.greys.white};
    ${(props) =>
      props.shape === CircularIndicatorType.FULL
        ? `
            height: 90%;
            width: 90%;
          `
        : `
            height: 75%;
            width: 75%;
          `}
  }
`

export const CircularProgressContainer = styled.div<{
  width?: number
  height?: number
  padding: Padding
}>`
  width: ${(props) => props.width || 250}px;
  height: ${(props) => props.height || 230}px;
  ${(props) => padding(props.padding)};
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  position: relative;
`

export const CircularWrapper = styled.div<{
  border?: {
    width?: string
    style?: string
    color?: Property.Color
  }
}>`
  position: relative;
  border: ${(props) => props.border?.width || '1px'}
    ${(props) => props.border?.style || 'solid'}
    ${(props) => props.border?.color || props.theme.colors.palette.greys.light};
`

export const BundlePriceWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const BundlePriceText = styled.span`
  ${Text('large1', 'alternate')};
  margin-bottom: ${spacing(1)};
`

export const BundlePrice = styled.span`
  ${Text('large1', 'default')};
`

export const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const AddToBasketButton = styled(Button)<{
  isCircularBar?: boolean
}>`
  min-width: 120px;
  width: fit-content;
  ${(props) =>
    props.isCircularBar &&
    `
     width: 100%;
     margin: ${spacing(2)} ${spacing(3)};
  `}
  button,
  button:hover,
  button:focus,
  button:disabled,
  button:active {
    padding-left: ${spacing(0.5)};
    padding-right: ${spacing(0.5)};
  }
`

export const ProgressBarTotalContentText = styled.span`
  ${Text('small', 'alternate')};
`
